import type { ScreenTabDataPublic, ScreenTabsDataPublic } from '../types';
import { isNotEmptyString } from '../utils/is-not-empty-string';

const isValidScreenTabId = (id: unknown, internalScreenTabs: ScreenTabsDataPublic) =>
	isNotEmptyString(id) && internalScreenTabs.find((tab) => tab.id === id) !== undefined;
const isValidScreenTabIsVisible = (isVisible: unknown) =>
	typeof isVisible === 'boolean' || typeof isVisible === 'undefined';
const isValidScreenTabIsActive = (isActive: unknown) =>
	typeof isActive === 'boolean' || typeof isActive === 'undefined';

// takes a partial screenTabsArray from the iframe that has been validated and merges it with the internal state.
export const screenTabsPublicToInternalTransformer = (
	screenTabsFromApp: ScreenTabsDataPublic,
	screenTabsInternal: ScreenTabsDataPublic,
): ScreenTabsDataPublic => {
	let numberOfActiveScreenTabs = 0;

	const internalActiveScreenTabIndex = screenTabsInternal.findIndex((tab) => tab.isActive === true);

	const screenTabsInternalUpdated = screenTabsInternal.map(
		(screenTabInternal: ScreenTabDataPublic) => {
			const screenTabFromApp = screenTabsFromApp.find((tab) => tab.id === screenTabInternal.id);
			if (!screenTabFromApp) {
				return screenTabInternal;
			}
			numberOfActiveScreenTabs += screenTabFromApp.isActive === true ? 1 : 0;
			return {
				id: screenTabInternal.id,
				isVisible: screenTabFromApp.isVisible ?? true,
				isActive: screenTabFromApp.isActive,
			};
		},
	);

	// if no active tab is specified. default to the first.
	if (
		numberOfActiveScreenTabs === 0 &&
		internalActiveScreenTabIndex === -1 &&
		typeof screenTabsInternalUpdated[0]?.isActive !== 'undefined'
	) {
		screenTabsInternalUpdated[0].isActive = true;
	}
	return screenTabsInternalUpdated;
};

export const screenTabsPublicToInternalValidator = (
	screenTabsFromApp: unknown, // a partial screentabs data containing only changed tabs.
	screenTabsInternal: ScreenTabsDataPublic,
): screenTabsFromApp is ScreenTabsDataPublic => {
	let numberOfActiveScreenTabs = 0;
	if (!Array.isArray(screenTabsFromApp)) {
		return false;
	}

	const screenTabsFromAppFiltered = screenTabsFromApp.filter(
		({ id, isVisible, isActive }: ScreenTabDataPublic) => {
			const isValidScreenTab =
				isValidScreenTabId(id, screenTabsInternal) &&
				isValidScreenTabIsVisible(isVisible) &&
				isValidScreenTabIsActive(isActive);
			numberOfActiveScreenTabs += isActive ? 1 : 0;
			return isValidScreenTab;
		},
	);

	return (
		numberOfActiveScreenTabs <= 1 && screenTabsFromApp.length === screenTabsFromAppFiltered.length
	);
};
